// src/components/MilestoneBadge.js
//
// ── Tonal Seal badge art ─────────────────────────────────────────────────
//
// One silhouette, six depths. Tiers escalate through the ADMIN'S OWN brand
// ramp (primary-light → content-card → feature-tile → primary-dark → primary)
// and change MATERIAL only at the very top (gold at 250 / 6 Months), so the
// shelf reads as a single collection that deepens rather than a bag of
// mismatched colours.
//
// Deliberately NOT wired to CSS custom properties: the ramp is a designed
// sequence whose steps must stay in a fixed relationship to each other. If it
// tracked a themeable --mom-primary, an admin colour change could collapse two
// adjacent tiers into the same value and destroy the progression. Same
// reasoning as the Strong+ tag colour.
//
// Pure SVG, no image assets, no emoji. See docs/decisions/adr-milestone-badges.md.

import React from 'react';
import { BADGE_BY_KEY } from '../services/milestones';

// Sampled from the admin's configured palette (Admin → Colors), so the badges
// sit in the same family as the rest of the app.
const RAMP = [
    { from: '#fbeff0', to: '#f0d6d4', ink: '#c05a7a', line: 'rgba(192,90,122,.38)' }, // 0
    { from: '#f0d6d4', to: '#d0ada4', ink: '#8a4a52', line: 'rgba(138,74,82,.34)' },  // 1
    { from: '#d9b9b2', to: '#c1848c', ink: '#ffffff', line: 'rgba(255,255,255,.5)' }, // 2
    { from: '#c99098', to: '#c05a7a', ink: '#ffffff', line: 'rgba(255,255,255,.52)' },// 3
    { from: '#cf6a8b', to: '#c33764', ink: '#ffffff', line: 'rgba(255,255,255,.55)' },// 4
    { from: '#e3c079', to: '#b8913f', ink: '#ffffff', line: 'rgba(255,255,255,.6)' }, // 5 — gold
];

const LOCKED_FILL = '#efe7e0';
const LOCKED_INK = '#a9a29c';
const LOCKED_LINE = 'rgba(138,131,125,.28)';

// A filled two-leaf seedling. First Step has no numeral worth showing ("1" is
// a weak thing to hand someone for starting), so it gets a mark instead.
function sproutPath(ink) {
    return (
        <g transform="translate(60,62)">
            <path d="M0 14 C 0 6 0 1 0 -4" fill="none" stroke={ink} strokeWidth="2" strokeLinecap="round" />
            <path d="M-0.7 1.5 C -11 0 -14.5 -9 -5.5 -12.5 C -1.8 -8 -1 -3 -0.7 1.5 Z" fill={ink} />
            <path d="M0.7 -3 C 10 -5.5 13 -14 4.5 -17 C 1.6 -12 0.9 -7 0.7 -3 Z" fill={ink} opacity="0.72" />
        </g>
    );
}

// ── Seasonal seal (The Gift of Showing Up 2026) ──────────────────────────
// Adapted from the owner's reference: a cream seal, a gold rim, a dotted inner
// ring and a gold star. The gold is the SAME pair as tier 5 (250 / 6 Months),
// so the seasonal badge reads as part of the collection, not a sticker from
// another set. Cream body (not the pink ramp) is what marks it as special.
//
// Two levels of detail from ONE silhouette: at shelf / Wall size the title
// lettering would be ~4px, so small renders carry the star + year only; the
// detail card and celebration (≥120px) show the full lettering of the
// reference. The badge name always sits under the art, so nothing is lost.
const GIFT = {
    bodyFrom: '#fdf8f3', bodyTo: '#f3e6da',
    goldFrom: '#e3c079', goldTo: '#b8913f',
    ink: '#6e5040',
};
const STAR_PTS = (cx, cy, R) => {
    const r = R * 0.42;
    const pts = [];
    for (let i = 0; i < 10; i += 1) {
        const rad = ((-90 + i * 36) * Math.PI) / 180;
        const len = i % 2 === 0 ? R : r;
        pts.push(`${(cx + len * Math.cos(rad)).toFixed(2)},${(cy + len * Math.sin(rad)).toFixed(2)}`);
    }
    return pts.join(' ');
};

function SeasonalSeal({ badge, size, locked, progress, title }) {
    const uid = `mb-${badge.key}${locked ? '-l' : ''}-${size >= 120 ? 'f' : 'c'}`;
    const full = size >= 120;
    const ink = locked ? LOCKED_INK : GIFT.ink;
    const gold = locked ? LOCKED_INK : `url(#${uid}-g)`;
    const rimR = 50;
    const C = 2 * Math.PI * rimR;
    const dash = Math.max(0, Math.min(1, progress)) * C;
    const year = (badge.windowEnd || '').slice(0, 4);
    const serifFont = 'var(--mom-font-serif, Georgia, serif)';
    const sansFont = 'var(--mom-font-sans, sans-serif)';

    return (
        <svg width={size} height={size} viewBox="0 0 120 120" role="img"
            aria-label={title || `${badge.name}${locked ? ' (not yet earned)' : ''}`}
            style={{ display: 'block' }}>
            <defs>
                <radialGradient id={`${uid}-f`} cx="38%" cy="30%" r="80%">
                    <stop offset="0%" stopColor={locked ? LOCKED_FILL : GIFT.bodyFrom} />
                    <stop offset="100%" stopColor={locked ? LOCKED_FILL : GIFT.bodyTo} />
                </radialGradient>
                <linearGradient id={`${uid}-g`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={GIFT.goldFrom} />
                    <stop offset="100%" stopColor={GIFT.goldTo} />
                </linearGradient>
                <filter id={`${uid}-d`} x="-30%" y="-30%" width="160%" height="160%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#8a6a3f" floodOpacity={locked ? '0.08' : '0.22'} />
                </filter>
            </defs>

            <g filter={`url(#${uid}-d)`}>
                <circle cx="60" cy="60" r="54" fill={`url(#${uid}-f)`} />
                <circle cx="60" cy="60" r={rimR} fill="none" stroke={locked ? LOCKED_LINE : gold} strokeWidth={full ? 2.2 : 2.8} />
                <circle cx="60" cy="60" r="44.5" fill="none" stroke={locked ? LOCKED_LINE : GIFT.goldTo}
                    strokeWidth={full ? 0.9 : 1.2} strokeDasharray={full ? '0.1 2.6' : '0.1 3.4'} strokeLinecap="round" opacity="0.75" />

                {full ? (
                    <>
                        <polygon points={STAR_PTS(60, 33, 12)} fill={gold} />
                        <text x="60" y="56" textAnchor="middle" fontFamily={serifFont} fontWeight="700" fontSize="8.6" letterSpacing="0.3" fill={ink}>THE GIFT OF</text>
                        <text x="60" y="69" textAnchor="middle" fontFamily={serifFont} fontWeight="700" fontSize="11" fill={ink}>SHOWING UP</text>
                        <line x1="38" y1="75" x2="82" y2="75" stroke={locked ? LOCKED_LINE : GIFT.goldFrom} strokeWidth="0.7" />
                        <text x="60" y="84.5" textAnchor="middle" fontFamily={sansFont} fontWeight="600" fontSize="5.6" letterSpacing="1.4" fill={ink}>{badge.threshold} CLASSES</text>
                        <text x="60" y="93" textAnchor="middle" fontFamily={sansFont} fontWeight="600" fontSize="5.6" letterSpacing="1.4" fill={ink}>{year}</text>
                    </>
                ) : (
                    <>
                        <polygon points={STAR_PTS(60, 53, 21)} fill={gold} />
                        <text x="60" y="85" textAnchor="middle" fontFamily={sansFont} fontWeight="700" fontSize="9.5" letterSpacing="2" fill={ink}>{year}</text>
                    </>
                )}

                {!locked && (
                    <ellipse cx="46" cy="34" rx="26" ry="14" fill="rgba(255,255,255,0.45)" transform="rotate(-24 46 34)" opacity="0.6" />
                )}
            </g>

            {locked && progress > 0 && (
                <circle cx="60" cy="60" r={rimR} fill="none" stroke="var(--mom-primary, #c33764)"
                    strokeWidth="2.6" strokeLinecap="round"
                    strokeDasharray={`${dash.toFixed(1)} ${C.toFixed(1)}`}
                    transform="rotate(-90 60 60)" opacity="0.75" />
            )}
        </svg>
    );
}

function MilestoneBadge({ badgeKey, size = 96, locked = false, progress = 0, title }) {
    const badge = BADGE_BY_KEY[badgeKey];
    if (!badge) return null;
    if (badge.family === 'seasonal') {
        return <SeasonalSeal badge={badge} size={size} locked={locked} progress={progress} title={title} />;
    }

    const uid = `mb-${badgeKey}${locked ? '-l' : ''}`;
    const isDial = badge.family === 'consistency';
    const t = RAMP[badge.tier] || RAMP[0];

    const fillFrom = locked ? LOCKED_FILL : t.from;
    const fillTo = locked ? LOCKED_FILL : t.to;
    const ink = locked ? LOCKED_INK : t.ink;
    const line = locked ? LOCKED_LINE : t.line;

    // Consistency badges carry a tick per qualifying week around the rim, so
    // the badge is literally countable: four ticks, twelve, twenty-six.
    const weeks = badge.threshold;
    const ticks = [];
    if (isDial) {
        for (let i = 0; i < weeks; i += 1) {
            const deg = -90 + (360 / weeks) * i;
            const a = (deg * Math.PI) / 180;
            // Quarter marks sit longer, the way a dial is indexed.
            const major = i % (weeks >= 26 ? 13 : weeks >= 12 ? 6 : 4) === 0;
            const inner = major ? 40 : 43;
            ticks.push(
                <line
                    key={i}
                    x1={(60 + inner * Math.cos(a)).toFixed(2)}
                    y1={(60 + inner * Math.sin(a)).toFixed(2)}
                    x2={(60 + 47 * Math.cos(a)).toFixed(2)}
                    y2={(60 + 47 * Math.sin(a)).toFixed(2)}
                    stroke={line}
                    strokeWidth={weeks > 20 ? 1.2 : 1.8}
                    strokeLinecap="round"
                />
            );
        }
    }

    const r = isDial ? 36 : 52;
    const innerR = isDial ? 30 : 45;

    // Locked badges show real progress on the rim rather than a padlock — a
    // trophy case with gaps you want to fill, not a wall of denial.
    const C = 2 * Math.PI * r;
    const dash = Math.max(0, Math.min(1, progress)) * C;

    const numeral = isDial ? String(Math.round(weeks / 4.333)) : String(badge.threshold);
    const fontSize = isDial ? 30 : numeral.length >= 3 ? 31 : 38;

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 120 120"
            role="img"
            aria-label={title || `${badge.name}${locked ? ' (not yet earned)' : ''}`}
            style={{ display: 'block' }}
        >
            <defs>
                <radialGradient id={`${uid}-f`} cx="34%" cy="26%" r="80%">
                    <stop offset="0%" stopColor={fillFrom} />
                    <stop offset="100%" stopColor={fillTo} />
                </radialGradient>
                <linearGradient id={`${uid}-s`} x1="0" y1="0" x2="0.3" y2="1">
                    <stop offset="0%" stopColor="rgba(255,255,255,.42)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </linearGradient>
                <filter id={`${uid}-d`} x="-30%" y="-30%" width="160%" height="160%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#8a4a52" floodOpacity={locked ? '0.08' : '0.20'} />
                </filter>
            </defs>

            {!locked && isDial && ticks}

            <g filter={`url(#${uid}-d)`}>
                <circle cx="60" cy="60" r={r} fill={`url(#${uid}-f)`} />
                <circle cx="60" cy="60" r={innerR} fill="none" stroke={line} strokeWidth="0.9" />
                {badge.tier >= 3 && !locked && (
                    <circle cx="60" cy="60" r={innerR + 2.5} fill="none" stroke={line} strokeWidth="0.6" opacity="0.6" />
                )}

                {badgeKey === 'first' ? sproutPath(ink) : (
                    <text
                        x="60"
                        y={isDial ? 56 : 60}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontFamily="var(--mom-font-serif, Georgia, serif)"
                        fontStyle="italic"
                        fontWeight="600"
                        fontSize={fontSize}
                        fill={ink}
                    >
                        {numeral}
                    </text>
                )}

                {isDial && (
                    <text
                        x="60"
                        y="76"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontFamily="var(--mom-font-sans, sans-serif)"
                        fontWeight="600"
                        fontSize="7.5"
                        letterSpacing="1.5"
                        fill={ink}
                        opacity="0.85"
                    >
                        {weeks} WEEKS
                    </text>
                )}

                {!locked && (
                    <ellipse cx="46" cy="36" rx="24" ry="15" fill={`url(#${uid}-s)`} transform="rotate(-24 46 36)" />
                )}
            </g>

            {locked && progress > 0 && (
                <circle
                    cx="60"
                    cy="60"
                    r={r}
                    fill="none"
                    stroke="var(--mom-primary, #c33764)"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeDasharray={`${dash.toFixed(1)} ${C.toFixed(1)}`}
                    transform="rotate(-90 60 60)"
                    opacity="0.75"
                />
            )}
        </svg>
    );
}

export default MilestoneBadge;
